/**
 * Hervat-checks bij terugkeer uit de achtergrond. Twee problemen die
 * alleen bij HERVATTEN bestaan: (1) het thema loopt achter, want de
 * prefers-color-scheme MediaQueryList vuurt niet met terugwerkende
 * kracht in een gesuspende WebView; (2) een verouderde bundel, want
 * er is bewust geen service worker. Na >= MIN_HIDDEN_MS wordt
 * index.html vers opgehaald en bij een andere bundelnaam herladen.
 * docs/customer-app.md > Front-end > Resume checks
 */
import { maybeRefreshSession } from "./api";
import { applyThemeFromStorage } from "./theme";

const MIN_HIDDEN_MS = 5 * 60 * 1000;

function currentBundle(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return el ? new URL(el.src, location.href).pathname : null;
}

function isNative(): boolean {
  const cap = (window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean };
  }).Capacitor;
  return Boolean(cap?.isNativePlatform?.());
}

async function maybeReloadOnNewBundle(): Promise<void> {
  // Native: de live-update-plugin doet zijn eigen hervat-check
  // (liveUpdate.ts); fetch("/") zou hier de LOKALE bundel vergelijken
  // en location.reload() laadt toch dezelfde lokale code.
  if (isNative()) return;
  const running = currentBundle();
  if (!running) return;
  try {
    const r = await fetch("/", { cache: "no-store" });
    if (!r.ok) return;
    const html = await r.text();
    const m = html.match(/\/assets\/[^"']+\.js/);
    if (m && m[0] !== running) {
      location.reload();
    }
  } catch {
    // offline of vliegtuigstand: gewoon doorgaan op de oude bundel
  }
}

export function initResumeChecks(): void {
  let hiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    applyThemeFromStorage();
    // Rollende sessie: een app die dagen in het geheugen hangt laadt
    // nooit opnieuw, dus ook hier het token verversen (no-op < 1 dag).
    void maybeRefreshSession();
    if (hiddenAt && Date.now() - hiddenAt >= MIN_HIDDEN_MS) {
      void maybeReloadOnNewBundle();
    }
  });
  // bfcache-herstel (iOS Safari terug-gebaar): visibilitychange kan
  // uitblijven, pageshow met persisted=true vuurt wél.
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted) applyThemeFromStorage();
  });
}
